import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PersonalDetailType, ProductType, VehicleState } from '../CommonTypes';
import { Journey } from '../../../consts/Common';
import { ButtonCommand } from '../Button/ButtonCommand';
import { getSelectedProducts } from '../ProductMethods';
import SummaryPersonalDetails from './SummaryPersonalDetails';
import SummaryVehicle from './SummaryVehicle';
import SummaryPlan from './SummaryPlan';
import SummaryQuote from './SummaryQuote';
import CustomerAcceptance from '../CustomerAcceptance';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import RenewalMarketingConsent from '../RenewalMarketingConsent';

export type SummaryCustomerAcceptance = {
  sections: string[];
};

type SummaryProps = {
  vehicle: VehicleState;
  personalDetail: PersonalDetailType;
  products: ProductType[];
  agreedTermsAndConditions: boolean;
  onUpdateAgreedTermsAndConditions: (newValue: boolean) => void;
  onUpdateProducts?: (
    updatedProducts: ProductType[],
    updateDocuments?: boolean
  ) => void;
  onPageChange: (page: string, status: boolean) => void;
  journeyType: number;
  policyStartDate: Date;
  error: string;
  onNavigateToQuoteRemove?: () => void;
  updateQuote?: (
    variantId: string,
    quoteTypeId: number,
    paymentMethod: string
  ) => void;
  isRenewals: boolean;
  showTermsAndConditions: boolean;
  vehiclePageUrl: string;
  personalDetailsPageUrl: string;
  productPageUrl: string;
  createPlanPageUrl: string;
  customerAcceptanceText: SummaryCustomerAcceptance;
  customerAcceptanceSectionExpandedOnLoad: boolean;
  customerAcceptanceTickboxText: string;
  renewalMarketingTickboxText: string;
  culture: string;
  phoneNumberLabel?: string;
};

type SummaryItemProps = {
  label: string;
  value: string;
};

export const SummaryItem = ({
  label,
  value
}: SummaryItemProps): JSX.Element => {
  return (
    <div className='flex-row'>
      <div className='flex-column bold'>
        {label.endsWith(':') ? label : `${label}:`}
      </div>
      <div className='flex-column'>{value}</div>
    </div>
  );
};

export const Summary = ({
  vehicle,
  personalDetail,
  products,
  agreedTermsAndConditions,
  onUpdateAgreedTermsAndConditions,
  onUpdateProducts,
  onPageChange,
  journeyType,
  policyStartDate,
  error,
  onNavigateToQuoteRemove,
  updateQuote,
  isRenewals,
  showTermsAndConditions,
  personalDetailsPageUrl,
  vehiclePageUrl,
  productPageUrl,
  createPlanPageUrl,
  customerAcceptanceText,
  customerAcceptanceSectionExpandedOnLoad,
  customerAcceptanceTickboxText,
  renewalMarketingTickboxText,
  culture,
  phoneNumberLabel
}: SummaryProps): JSX.Element => {
  const [expandedTermsConditions, setExpandedTermsConditions] = useState(
    customerAcceptanceSectionExpandedOnLoad
  );
  const navigate = useNavigate();
  const { t } = useTranslation();

  const customerAcceptanceSectionTitle = t(
    'summary.customer-acceptance-section-title'
  );
  const vehicleSectionTitle = t('summary.vehicle-section-title');
  const buttonEdit = t('summary.button-edit');
  const productsSelectedTitle = t('summary.products-selected-title');

  const [agreedCustomerAcceptance, setAgreedCustomerAcceptance] = useState(
    agreedTermsAndConditions
  );

  const [isCustomerAcceptanceCollapsed, setIsCustomerAcceptanceCollapsed] =
    useState(false);

  useEffect(() => {
    onUpdateAgreedTermsAndConditions(agreedCustomerAcceptance);
  }, [agreedCustomerAcceptance]);

  let selectedQuotes: ProductType[] = [];
  if (products && products !== undefined)
    selectedQuotes = getSelectedProducts(products);

  const removeProduct = (productTypeId: number) => {
    const updatedProducts = products.map((product) =>
      product.productTypeId === productTypeId
        ? {
            ...product,
            isSelected: false,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: undefined,
            selectedPaymentMethod: undefined,
            productVariants: product.productVariants.map((variant) =>
              variant.isSelected === true
                ? { ...variant, isSelected: false }
                : variant
            )
          }
        : product
    );

    if (onUpdateProducts) onUpdateProducts(updatedProducts, true);

    const selectedProducts = getSelectedProducts(updatedProducts);

    if (selectedProducts.length === 0) {
      navigate(productPageUrl);
      onPageChange(productPageUrl, false);
    }
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onNavigateToPersonalDetailsPage = () => {
    navigate(personalDetailsPageUrl);
    goToTop();
  };
  const onNavigateToVehicle = () => {
    navigate(vehiclePageUrl);
    goToTop();
  };
  const onNavigateToCreatePlan = () => {
    if (CommonValidation.isNotEmpty(createPlanPageUrl)) {
      navigate(createPlanPageUrl);
      goToTop();
    }
  };
  const [expanded, setExpanded] = useState<string | false>('');

  useEffect(() => {
    setAgreedCustomerAcceptance(false);
    if (isRenewals) {
      setExpanded('planPanel');
    }
  }, [error]);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const renderPersonalDetailsSection = (title: string, className: string) => {
    return (
      <Accordion
        expanded={expanded === 'customerPanel'}
        onChange={handleChange('customerPanel')}
        className={className}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='customerPaneld-content'
          id='customerPaneld-header'>
          <p className={'p1'}>
            {title}
            {expanded === 'customerPanel' && (
              <ButtonCommand
                name='btn-edit'
                text={buttonEdit}
                additionalClassNames='m05-left small'
                onClick={onNavigateToPersonalDetailsPage}
              />
            )}
          </p>
        </AccordionSummary>
        <AccordionDetails>
          <SummaryPersonalDetails
            personalDetail={personalDetail}
            isConfirmationScreen={false}
            culture={culture}
            phoneNumberLabel={phoneNumberLabel}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderVehicleSection = (className: string) => {
    return (
      <Accordion
        expanded={expanded === 'vehiclePanel'}
        onChange={handleChange('vehiclePanel')}
        className={className}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='vehiclePaneld-content'
          id='vehiclePaneld-header'>
          <p className={'p1'}>
            {vehicleSectionTitle}
            {expanded === 'vehiclePanel' && (
              <ButtonCommand
                name='btn-edit'
                text={buttonEdit}
                additionalClassNames='m05-left small'
                onClick={onNavigateToVehicle}
              />
            )}
          </p>
        </AccordionSummary>
        <AccordionDetails>
          <SummaryVehicle
            vehicle={vehicle}
            journeyType={journeyType}
            culture={culture}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderPolicySection = () => {
    if (isRenewals) {
      return (
        <div>
          <p className='p3 m1-bottom'>{productsSelectedTitle}</p>
          {selectedQuotes.map((quote: ProductType) => (
            <SummaryQuote
              key={`quote-${quote.productId}`}
              quote={quote}
              policyStartDate={policyStartDate}
              isPaymentDone={false}
              error={error}
              update={updateQuote}
              remove={onNavigateToQuoteRemove}
              culture={culture}
            />
          ))}
        </div>
      );
    } else {
      return (
        <SummaryPlan
          products={products}
          removeProduct={removeProduct}
          showRemove
          showTitle
          showEdit
          editProduct={onNavigateToCreatePlan}
          culture={culture}
        />
      );
    }
  };

  const renderSections = () => {
    const title =
      journeyType === Journey.Renewals
        ? t('summary.personal-details-section-title')
        : t('summary.customer-details-section-title');
    if (isRenewals) {
      return (
        <>
          {renderPersonalDetailsSection(title, '')}
          {renderVehicleSection('accordion-margintop')}
        </>
      );
    } else {
      return (
        <>
          {renderVehicleSection('')}
          {renderPersonalDetailsSection(title, 'accordion-margintop')}
        </>
      );
    }
  };

  const onExpandClick = () => {
    if (agreedTermsAndConditions && !isCustomerAcceptanceCollapsed) {
      setIsCustomerAcceptanceCollapsed(true);
      return false;
    } else {
      setIsCustomerAcceptanceCollapsed(false);
      return true;
    }
  };

  return (
    <div>
      <div className='m1-bottom'>
        <div>{renderSections()}</div>
        <Accordion
          expanded={expanded === 'planPanel'}
          onChange={handleChange('planPanel')}
          className='accordion-margintop'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='planPaneld-content'
            id='planPaneld-header'>
            <p className={'p1'}>
              {isRenewals
                ? t('summary.policy-details-section-title')
                : t('summary.customer-plan-section-title')}
            </p>
          </AccordionSummary>
          <AccordionDetails>{renderPolicySection()}</AccordionDetails>
        </Accordion>
      </div>

      {showTermsAndConditions && (
        <div>
          <Accordion
            expanded={expandedTermsConditions}
            onClick={() => setExpandedTermsConditions(() => onExpandClick())}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='termsConditionsPaneld-content'
              id='termsConditionsPaneld-header'>
              <p className={'p1'}>{customerAcceptanceSectionTitle}</p>
            </AccordionSummary>
            <AccordionDetails>
              <CustomerAcceptance
                customerAcceptanceText={customerAcceptanceText.sections}
                setAgreedCustomerAcceptance={setAgreedCustomerAcceptance}
                customerAcceptanceTickboxText={customerAcceptanceTickboxText}
              />
              {renewalMarketingTickboxText && (
                <RenewalMarketingConsent
                  renewalMarketingTickboxText={renewalMarketingTickboxText}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default Summary;
